<template>
  <div class="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8 flex justify-center">
    <img
      :src="vectorArt"
      alt="Background Vector"
      class="absolute left-20 top-1/2 -translate-y-1/2
             w-72 md:w-96
             opacity-25
             pointer-events-none
             z-10"
    />
    <img
      :src="vectorArt"
      alt="Background Vector"
      class="absolute right-40 bottom-20
             w-72 md:w-96
             opacity-25
             pointer-events-none
             z-10"
    />
    <div class="w-full max-w-3xl bg-white rounded-xl shadow-lg p-8 z-20">
      <!-- Header -->
      <h2 class="text-3xl font-bold text-gray-900 mb-8 text-center">Suggestions</h2>

      <!-- Notification/Message Banner -->
      <transition name="fade">
        <div 
          v-if="message" 
          :class="[
            'p-3 text-center text-sm font-semibold mb-6 rounded-lg transition-opacity duration-300',
            messageType === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          ]"
        >
          {{ message }}
        </div>
      </transition>
      
      <!-- Suggestions List -->
      <div class="space-y-6 mb-8">
        <div v-if="loadingSuggestions" class="text-center p-8 text-gray-500">
          <svg class="animate-spin h-5 w-5 mr-3 inline text-blue-500" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading suggestions...
        </div>
        <div v-else-if="comments.length === 0" class="text-center p-8 text-gray-500 border border-dashed rounded-lg">
          No suggestions posted yet. Be the first!
        </div>
        <!-- Note the use of comment.user_email and comment.suggestion_text from the API -->
        <div v-else v-for="comment in comments" :key="comment.id" class="border-b border-gray-200 pb-6">
          <div class="flex items-center mb-4">
            <div class="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
              {{ comment.user_email.charAt(0).toUpperCase() }}
            </div>
            <div class="ml-4">
              <p class="text-lg font-medium text-gray-900">{{ comment.user_email }}</p>
              <p class="text-sm text-gray-500">{{ formatDate(comment.created_at) }}</p>
            </div>
          </div>
          <p class="text-gray-700">{{ comment.suggestion_text }}</p>
        </div>
      </div>

      <!-- Comment Input Form or Login Message -->
      <div>
        <h3 class="text-xl font-semibold text-gray-900 mb-4">Post a Comment</h3>
        
        <div v-if="isLoggedIn">
          <form @submit.prevent="postComment">
            
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700">
                Commenting as: <span class="font-bold text-blue-600">{{ currentUser.email || 'User' }}</span>
              </label>
            </div>

            <div class="mb-4">
              <label for="comment" class="block text-sm font-medium text-gray-700">Suggestion</label>
              <textarea
                v-model="newComment.text"
                id="comment"
                rows="4"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-600 focus:ring-blue-600 py-2 px-3"
                placeholder="Share your suggestion..."
                required
              ></textarea>
            </div>
            <button
              type="submit"
              :disabled="posting"
              class="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ posting ? 'Posting...' : 'Post Suggestion' }}
            </button>
          </form>
        </div>
        
        <div v-else class="text-center p-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
          <p class="text-lg text-red-500 font-semibold mb-2">Login Required</p>
          <p class="text-gray-600">You must be logged in to post a comment or suggestion.</p>
          <a href="/login" class="mt-4 inline-block text-blue-600 hover:text-blue-700 font-medium">
            Go to Login Page
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import vectorArt from '@/assets/images/art33.png'
// Utility function to check authentication status from localStorage
function getAuthStatus() {
  const token = localStorage.getItem("token");
  const userString = localStorage.getItem("user");
  
  if (token && userString) {
    try {
      // Parse the user object saved during login
      return { isLoggedIn: true, user: JSON.parse(userString) };
    } catch (e) {
      console.error("Error parsing user data from localStorage:", e);
      return { isLoggedIn: false, user: null };
    }
  }
  return { isLoggedIn: false, user: null };
}

export default {
  data() {
    return {
      vectorArt,
      comments: [], // Initialized as empty, will be populated by API
      newComment: {
        text: ""
      },
      // State properties for authentication
      isLoggedIn: false,
      currentUser: {},
      loadingSuggestions: true, // New state for loading indicator
      posting: false, // New state for post button status
      message: "", // New state for user feedback messages
      messageType: "error", // 'success' or 'error'
      // IMPORTANT: Replace this with your actual API base URL if different
      baseUrl: "https://sanskritirdapoon.in", 
    };
  },
  mounted() {
    // Initial check for authentication status
    this.updateAuthStatus();
    this.fetchSuggestions(); // Fetch existing suggestions from the DB
    
    // Listen for storage changes (e.g., login/logout in another tab)
    window.addEventListener('storage', this.updateAuthStatus);
  },
  beforeUnmount() {
    // Clean up the event listener to prevent memory leaks
    window.removeEventListener('storage', this.updateAuthStatus);
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    showMessage(msg, type = "error") {
      this.message = msg;
      this.messageType = type;
      setTimeout(() => {
        this.message = "";
      }, 4000); // Clear message after 4 seconds
    },
    updateAuthStatus() {
      const { isLoggedIn, user } = getAuthStatus();
      this.isLoggedIn = isLoggedIn;
      // Set currentUser to the parsed user object or an empty object if logged out
      this.currentUser = user || {}; 
    },
    
    // New method to fetch suggestions from the API
    async fetchSuggestions() {
      this.loadingSuggestions = true;
      try {
        const res = await fetch(`${this.baseUrl}/api/suggestions`);
        if (res.ok) {
          // Assuming API returns an array of objects matching your DB structure
          const allSuggestions = await res.json();
          // Filter only approved suggestions
          this.comments = allSuggestions.filter(s => s.is_approved);
        } else {
          const data = await res.json();
          this.showMessage(data.error || 'Failed to load suggestions.', 'error');
        }
      } catch (err) {
        console.error("Fetch suggestions error:", err);
        this.showMessage('Connection error while fetching suggestions.', 'error');
      } finally {
        this.loadingSuggestions = false;
      }
    },
    
    // Updated method to post comments to the API
    async postComment() {
      if (!this.isLoggedIn || !this.currentUser.email || !this.newComment.text) {
          this.showMessage("You must be logged in and provide text.", "error");
          return;
      }

      this.posting = true;
      
      const payload = {
        user_email: this.currentUser.email, // Sent to the server for insertion
        text: this.newComment.text,
      };

      try {
        const res = await fetch(`${this.baseUrl}/api/suggestions`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await res.json();

        if (res.ok) {
          this.showMessage(data.message || 'Suggestion posted!', 'success');
          this.newComment.text = "";
          
          // Prepend the new suggestion from the API response to the list
          this.comments.unshift({
            id: data.id,
            user_email: data.user_email,
            suggestion_text: data.text,
            created_at: new Date().toISOString() // Use a placeholder date for immediate rendering
          });
          
        } else {
          this.showMessage(data.error || 'Failed to post suggestion to the database.', 'error');
        }
      } catch (err) {
        console.error("Post suggestion error:", err);
        this.showMessage('Network error while posting suggestion.', 'error');
      } finally {
        this.posting = false;
      }
    }
  }
};
</script>

<style scoped>
/* Tailwind CSS is used directly in the template, so no additional styles are needed here */
</style>